
import { useState } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { Button } from "../ui/button";
import LeadFormInput from "./LeadFormInput";
import ContactMethodSelect from "./ContactMethodSelect";
import { useToast } from "../ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface LeadFormProps {
  onSubmitSuccess?: () => void;
}

const LeadForm = ({ onSubmitSuccess }: LeadFormProps) => {
  const [leadInfo, setLeadInfo] = useState({
    email: '',
    phone: '',
    company: '',
    preferredContact: 'email'
  });
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await supabase.from('leads').insert([leadInfo]);
      toast({
        title: "Thanks for your interest! 🎉",
        description: "We'll be in touch with you shortly!",
      });
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast({
        title: "Error",
        description: "Failed to submit your information. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="mt-6 space-y-4"
      onSubmit={handleSubmit}
    >
      <LeadFormInput
        type="email"
        placeholder="Your email address"
        value={leadInfo.email}
        onChange={(value) => setLeadInfo(prev => ({ ...prev, email: value }))}
        required
      />
      <LeadFormInput
        type="tel"
        placeholder="Phone number (optional)"
        value={leadInfo.phone}
        onChange={(value) => setLeadInfo(prev => ({ ...prev, phone: value }))}
      />
      <LeadFormInput
        type="text"
        placeholder="Company name"
        value={leadInfo.company}
        onChange={(value) => setLeadInfo(prev => ({ ...prev, company: value }))}
        required
      />
      <ContactMethodSelect
        value={leadInfo.preferredContact}
        onChange={(value) => setLeadInfo(prev => ({ ...prev, preferredContact: value }))}
      />
      <Button 
        type="submit" 
        className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20"
      >
        <Send className="w-4 h-4 mr-2" />
        Submit
      </Button>
    </motion.form>
  );
};

export default LeadForm;
